// =====================================================================================
// FILE: src/components/ui/StickyHorizontalScroll.jsx
// Sticky horizontal scrollbar that stays at the bottom of the viewport
// =====================================================================================
import React, { useEffect, useRef, useState, useCallback } from "react";

export default function StickyHorizontalScroll({ containerRef }) {
  const scrollbarRef = useRef(null);
  const thumbRef = useRef(null);
  const [scrollInfo, setScrollInfo] = useState({
    scrollWidth: 0,
    clientWidth: 0,
    scrollLeft: 0,
    visible: false,
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartScrollLeft = useRef(0);

  // Update scroll info when container changes
  const updateScrollInfo = useCallback(() => {
    if (!containerRef?.current) return;
    
    const { scrollWidth, clientWidth, scrollLeft } = containerRef.current;
    const needsScroll = scrollWidth > clientWidth + 10; // Small buffer
    
    setScrollInfo({
      scrollWidth,
      clientWidth,
      scrollLeft,
      visible: needsScroll,
    });
  }, [containerRef]);

  // Listen to container scroll
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    updateScrollInfo();
    container.addEventListener("scroll", updateScrollInfo);
    window.addEventListener("resize", updateScrollInfo);

    // Also observe for content changes
    const observer = new MutationObserver(updateScrollInfo);
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      container.removeEventListener("scroll", updateScrollInfo);
      window.removeEventListener("resize", updateScrollInfo);
      observer.disconnect();
    };
  }, [containerRef, updateScrollInfo]);

  // Handle clicking on track to jump
  const handleTrackClick = (e) => {
    if (!containerRef?.current || !scrollbarRef.current || e.target === thumbRef.current) return;
    
    const trackRect = scrollbarRef.current.getBoundingClientRect();
    const clickX = e.clientX - trackRect.left;
    const trackWidth = trackRect.width;
    const scrollRatio = clickX / trackWidth;
    
    containerRef.current.scrollLeft = scrollRatio * (scrollInfo.scrollWidth - scrollInfo.clientWidth);
  };

  // Handle thumb drag
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartX.current = e.clientX;
    dragStartScrollLeft.current = containerRef.current?.scrollLeft || 0;
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      if (!containerRef?.current || !scrollbarRef.current) return;
      
      const trackWidth = scrollbarRef.current.clientWidth;
      const thumbWidth = (scrollInfo.clientWidth / scrollInfo.scrollWidth) * trackWidth;
      const availableTrackWidth = trackWidth - thumbWidth;
      const maxScroll = scrollInfo.scrollWidth - scrollInfo.clientWidth;
      
      const deltaX = e.clientX - dragStartX.current;
      const scrollDelta = (deltaX / availableTrackWidth) * maxScroll;
      
      containerRef.current.scrollLeft = dragStartScrollLeft.current + scrollDelta;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, containerRef, scrollInfo]);

  // Don't render if no horizontal scroll needed
  if (!scrollInfo.visible) return null;

  // Calculate thumb size and position
  const thumbWidth = Math.max(
    50, // Minimum thumb width
    (scrollInfo.clientWidth / scrollInfo.scrollWidth) * 100
  );
  const thumbLeft = (scrollInfo.scrollLeft / (scrollInfo.scrollWidth - scrollInfo.clientWidth)) * (100 - thumbWidth);

  return (
    <div
      ref={scrollbarRef}
      onClick={handleTrackClick}
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: 16,
        background: "#E5E7EB",
        borderTop: "1px solid #D1D5DB",
        cursor: "pointer",
        zIndex: 9990,
        display: "flex",
        alignItems: "center",
        padding: "0 4px",
      }}
    >
      {/* Track area */}
      <div style={{
        position: "relative",
        flex: 1,
        height: 10,
        background: "#E5E7EB",
        borderRadius: 5,
      }}>
        {/* Thumb */}
        <div
          ref={thumbRef}
          onMouseDown={handleThumbMouseDown}
          style={{
            position: "absolute",
            top: 0,
            left: `${thumbLeft}%`,
            width: `${thumbWidth}%`,
            height: "100%",
            background: isDragging ? "#2a2a2a" : "#141414",
            borderRadius: 5,
            cursor: isDragging ? "grabbing" : "grab",
            transition: isDragging ? "none" : "background 0.15s ease",
          }}
        />
      </div>
    </div>
  );
}
